import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Button, Checkbox, ListItemText, Menu, MenuItem } from '@mui/material';
import { useId, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { PortPickerEntry } from '../../../utils/electronicsDashboardUtils';

export interface ChoosePortProps {
  entries: PortPickerEntry[];
  selectedPorts: number[];
  onSelectedPortsChange: (ports: number[]) => void;
  disabled?: boolean;
  chooseButtonI18nKey: string;
  chooseButtonDefault: string;
  chooseAllI18nKey: string;
  chooseAllDefault: string;
  /** When `drawer`, styles the trigger for mechanism-data drawer panels. */
  variant?: 'toolbar' | 'drawer';
}

/** Stable React key when multiple picker rows share a drive-unit port (invalid occupancy). */
export function portPickerMenuItemKey(entry: PortPickerEntry, index: number): string {
  return `${entry.port}:${entry.sectionTitle}:${index}`;
}

const ChoosePort = ({
  entries,
  selectedPorts,
  onSelectedPortsChange,
  disabled,
  chooseButtonI18nKey,
  chooseButtonDefault,
  chooseAllI18nKey,
  chooseAllDefault,
  variant = 'toolbar',
}: ChoosePortProps) => {
  const { t } = useTranslation();
  const chooseButtonId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const availablePorts = useMemo(() => [...new Set(entries.map((entry) => entry.port))].sort((a, b) => a - b), [entries]);
  const selectedSet = useMemo(() => new Set(selectedPorts), [selectedPorts]);
  const allSelected = availablePorts.length > 0 && availablePorts.every((port) => selectedSet.has(port));
  const someSelected = availablePorts.some((port) => selectedSet.has(port));

  if (entries.length <= 1) return null;

  const commitPorts = (ports: number[]) => {
    const unique = [...new Set(ports)].filter((port) => availablePorts.includes(port)).sort((a, b) => a - b);
    if (!unique.length) return;
    onSelectedPortsChange(unique);
  };

  const handleToggleAll = () => {
    commitPorts(allSelected ? [availablePorts[0]] : availablePorts);
  };

  const handleTogglePort = (port: number) => {
    const next = selectedSet.has(port) ? selectedPorts.filter((value) => value !== port) : [...selectedPorts, port];
    if (!next.length) return;
    commitPorts(next);
  };

  return (
    <>
      <Button
        id={chooseButtonId}
        size="small"
        color="inherit"
        variant="text"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          flexShrink: 0,
          minWidth: 0,
          px: 1,
          textTransform: 'none',
          ...(variant === 'toolbar' ? {} : { color: 'text.primary', alignSelf: 'flex-start', mb: 0.5 }),
        }}
      >
        <Trans i18nKey={chooseButtonI18nKey}>{chooseButtonDefault}</Trans>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ list: { 'aria-labelledby': chooseButtonId, dense: true, sx: { minWidth: 280 } } }}
      >
        <MenuItem onClick={handleToggleAll}>
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            tabIndex={-1}
            disableRipple
          />
          <ListItemText primary={t(chooseAllI18nKey, chooseAllDefault)} />
        </MenuItem>
        {entries.map((entry, index) => (
          <MenuItem key={portPickerMenuItemKey(entry, index)} onClick={() => handleTogglePort(entry.port)}>
            <Checkbox
              checked={selectedSet.has(entry.port)}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={entry.sectionTitle} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ChoosePort;
